import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import { productService } from "../../utils/services/productService";
import {
  filterScopeOptions,
  optionPrimaryLabel,
  optionsFromCatalogProductRows,
  optionsFromCatalogSkuRows,
} from "../../utils/lostSalesScopeOptions";

const SEARCH_DEBOUNCE_MS = 250;
const CATALOG_PAGE_SIZE = 15;
const ROW_HEIGHT = 44;

const fetchCatalogPage = (grain, clientId, search, skip, limit = CATALOG_PAGE_SIZE) =>
  grain === "product"
    ? productService
        .fetchNames(search, clientId, limit, skip)
        .then((response) => ({
          raw: response?.data || (Array.isArray(response) ? response : []),
          toOptions: optionsFromCatalogProductRows,
        }))
    : productService
        .fetchSKU(clientId, search, limit, skip)
        .then((response) => ({
          raw: response?.data || (Array.isArray(response) ? response : []),
          toOptions: optionsFromCatalogSkuRows,
        }));

/** Catalog search (server-side, paged); stale responses are dropped. */
const useCatalogSearch = ({ grain, clientId, search, enabled }) => {
  const [state, setState] = useState({
    options: [],
    loading: false,
    error: "",
    hasMore: false,
    skip: 0,
  });
  const requestSeq = useRef(0);

  const load = useCallback(
    async (skip) => {
      const seq = ++requestSeq.current;
      setState((prev) => ({
        ...prev,
        loading: true,
        error: "",
        ...(skip === 0 ? { options: [], hasMore: false } : {}),
      }));
      try {
        const { raw, toOptions } = await fetchCatalogPage(
          grain,
          clientId,
          search || undefined,
          skip,
        );
        if (seq !== requestSeq.current) return;
        const page = toOptions(raw);
        setState((prev) => {
          const base = skip === 0 ? [] : prev.options;
          const seen = new Set(base.map((option) => option.key));
          return {
            options: [...base, ...page.filter((option) => !seen.has(option.key))],
            loading: false,
            error: "",
            hasMore: raw.length === CATALOG_PAGE_SIZE,
            skip: skip + raw.length,
          };
        });
      } catch (_error) {
        if (seq !== requestSeq.current) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: "Catalog search failed.",
        }));
      }
    },
    [grain, clientId, search],
  );

  useEffect(() => {
    if (!enabled || !clientId) {
      requestSeq.current += 1;
      setState({ options: [], loading: false, error: "", hasMore: false, skip: 0 });
      return;
    }
    load(0);
  }, [enabled, clientId, load]);

  return {
    ...state,
    loadMore: () => {
      if (!state.loading && state.hasMore) load(state.skip);
    },
    retry: () => load(0),
  };
};

const SectionHeader = ({ children }) => (
  <Typography
    sx={{
      px: 1.5,
      pt: 1,
      pb: 0.5,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: "var(--text-tertiary)",
    }}
  >
    {children}
  </Typography>
);

const StatusLine = ({ children, spinner = false, action }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1 }}>
    {spinner && <CircularProgress size={14} />}
    <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)", flex: 1 }}>
      {children}
    </Typography>
    {action}
  </Box>
);

// Buttons inside the menu must not steal focus from the search input.
const keepFocus = (event) => event.preventDefault();

/**
 * Multi-select combobox for Lost Sales analysis scopes (SKUs, or products for
 * product-level clients).
 *
 * - The field itself is the search input: click and type.
 * - Options come from the catalog search (server-side, paged); "Select all
 *   matching" fetches every match of the search text in one request.
 * - Every toggle commits immediately, so the chips always match what will be
 *   analyzed, and closing always clears the search.
 */
const LostSalesScopePicker = ({
  grain = "sku",
  clientId,
  value = [],
  onChange,
  maxSelections = 200,
  disabled = false,
  limitTags = 2,
}) => {
  const isProduct = grain === "product";
  const noun = isProduct ? "products" : "SKUs";

  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectAllBusy, setSelectAllBusy] = useState(false);
  const [selectAllNotice, setSelectAllNotice] = useState("");

  useEffect(() => {
    const handle = setTimeout(
      () => setDebouncedQuery(query.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [query]);

  const catalog = useCatalogSearch({
    grain,
    clientId,
    search: debouncedQuery,
    enabled: open && !showSelectedOnly,
  });

  const selectedKeys = useMemo(
    () => new Set(value.map((option) => option.key)),
    [value],
  );

  const rows = useMemo(
    () => (showSelectedOnly ? filterScopeOptions(value, query) : catalog.options),
    [showSelectedOnly, value, query, catalog.options],
  );

  useEffect(() => {
    setActiveIndex(0);
    setSelectAllNotice("");
  }, [query, showSelectedOnly]);

  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.querySelector(`[data-row-index="${activeIndex}"]`);
    node?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  const atCapacity = value.length >= maxSelections;

  const toggle = (option) => {
    if (selectedKeys.has(option.key)) {
      onChange(value.filter((item) => item.key !== option.key));
      return;
    }
    if (atCapacity) return;
    onChange([...value, option]);
  };

  const trimmedQuery = query.trim();
  const canSelectAll =
    !showSelectedOnly && trimmedQuery.length > 0 && !atCapacity && !selectAllBusy;

  // One request for every match (+1 to detect overflow), not just the
  // loaded page.
  const selectAll = async () => {
    if (!canSelectAll) return;
    setSelectAllNotice("");
    setSelectAllBusy(true);
    try {
      const { raw, toOptions } = await fetchCatalogPage(
        grain,
        clientId,
        trimmedQuery,
        0,
        maxSelections + 1,
      );
      const current = valueRef.current;
      const currentKeys = new Set(current.map((option) => option.key));
      const additions = toOptions(raw).filter((option) => !currentKeys.has(option.key));
      if (
        raw.length > maxSelections ||
        current.length + additions.length > maxSelections
      ) {
        setSelectAllNotice(
          `More than ${maxSelections - current.length} ${noun} match “${trimmedQuery}”. Narrow your search.`,
        );
        return;
      }
      if (!additions.length) {
        setSelectAllNotice(`No more ${noun} match “${trimmedQuery}”.`);
        return;
      }
      onChange([...current, ...additions]);
    } catch (_error) {
      setSelectAllNotice("Couldn't load every match. Try again.");
    } finally {
      setSelectAllBusy(false);
    }
  };

  const openMenu = (selectedOnly = false) => {
    if (disabled) return;
    setShowSelectedOnly(selectedOnly);
    setOpen(true);
    inputRef.current?.focus();
  };

  const close = () => {
    setOpen(false);
    setQuery("");
    setDebouncedQuery("");
    setShowSelectedOnly(false);
    setSelectAllNotice("");
  };

  const onInputKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      setActiveIndex((index) => Math.min(index + 1, Math.max(rows.length - 1, 0)));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      const option = rows[activeIndex];
      if (option) toggle(option);
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        close();
      }
    } else if (event.key === "Tab") {
      if (open) close();
    } else if (event.key === "Backspace" && !query && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  // While typing, give the input the room; chips come back on close.
  const showTags = !(open && query);
  const visibleTags = value.slice(0, limitTags);
  const hiddenCount = value.length - visibleTags.length;

  const renderRow = (option, index) => {
    const checked = selectedKeys.has(option.key);
    const blocked = !checked && atCapacity;
    const primary = isProduct ? option.productName || option.productId : option.sku;
    const secondary = isProduct
      ? `${option.skus.length} ${option.skus.length === 1 ? "SKU" : "SKUs"}`
      : option.productName;
    return (
      <Box
        key={option.key}
        role="option"
        aria-selected={checked}
        aria-disabled={blocked || undefined}
        data-row-index={index}
        onMouseEnter={() => setActiveIndex(index)}
        onMouseDown={keepFocus}
        onClick={() => !blocked && toggle(option)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minHeight: ROW_HEIGHT,
          px: 1,
          cursor: blocked ? "not-allowed" : "pointer",
          opacity: blocked ? 0.5 : 1,
          backgroundColor: index === activeIndex ? "action.hover" : "transparent",
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          tabIndex={-1}
          disableRipple
          inputProps={{ "aria-label": primary }}
          sx={{ p: 0.5 }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 500 }} title={primary}>
            {primary}
          </Typography>
          {secondary && (
            <Typography
              noWrap
              sx={{ fontSize: 11.5, color: "var(--text-secondary)" }}
              title={secondary}
            >
              {secondary}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderEmpty = () => {
    if (showSelectedOnly) {
      return (
        <StatusLine>
          {query ? `No selected ${noun} match “${query}”.` : `No ${noun} selected yet.`}
        </StatusLine>
      );
    }
    if (catalog.loading) return <StatusLine spinner>Searching…</StatusLine>;
    if (catalog.error) {
      return (
        <StatusLine
          action={
            <Button
              size="small"
              onMouseDown={keepFocus}
              onClick={catalog.retry}
              sx={{ textTransform: "none" }}
            >
              Retry
            </Button>
          }
        >
          {catalog.error}
        </StatusLine>
      );
    }
    if (trimmedQuery !== debouncedQuery) return null;
    return (
      <StatusLine>
        {debouncedQuery ? `No ${noun} match “${debouncedQuery}”.` : `No ${noun} found.`}
      </StatusLine>
    );
  };

  return (
    <ClickAwayListener onClickAway={() => open && close()}>
      <Box sx={{ minWidth: 0 }}>
        <Box
          ref={fieldRef}
          data-testid="lost-sales-scope-picker"
          onMouseDown={(event) => {
            // Clicking anywhere on the field focuses the input and opens.
            if (event.target !== inputRef.current) event.preventDefault();
          }}
          onClick={() => openMenu(showSelectedOnly && open)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: 40,
            px: 1,
            border: "1px solid",
            borderColor: open ? "var(--accent)" : "var(--border-default, var(--border-color))",
            boxShadow: open ? "0 0 0 2px rgba(46,95,232,0.15)" : "none",
            borderRadius: "8px",
            backgroundColor: "var(--bg-paper)",
            cursor: disabled ? "default" : "text",
            opacity: disabled ? 0.6 : 1,
            minWidth: 0,
            "&:hover": { borderColor: disabled || open ? undefined : "var(--text-tertiary)" },
          }}
        >
          {showTags && value.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {visibleTags.map((option) => {
                const label = optionPrimaryLabel(option);
                return (
                  <Chip
                    key={option.key}
                    size="small"
                    label={label}
                    title={label}
                    onDelete={
                      disabled
                        ? undefined
                        : () => onChange(value.filter((item) => item.key !== option.key))
                    }
                    onClick={(event) => event.stopPropagation()}
                    sx={{ height: 24, fontSize: 11, maxWidth: 160, flexShrink: 0 }}
                  />
                );
              })}
              {hiddenCount > 0 && (
                <Chip
                  size="small"
                  label={`+${hiddenCount} more`}
                  onClick={(event) => {
                    event.stopPropagation();
                    openMenu(true);
                  }}
                  sx={{ height: 24, fontSize: 11, fontWeight: 600, flexShrink: 0 }}
                />
              )}
            </Box>
          )}
          <InputBase
            inputRef={inputRef}
            value={query}
            disabled={disabled}
            placeholder={
              value.length && showTags
                ? open
                  ? "Search…"
                  : ""
                : isProduct
                  ? "Search products to analyze"
                  : "Search SKUs or product names to analyze"
            }
            onChange={(event) => {
              setQuery(event.target.value);
              if (!open) {
                setShowSelectedOnly(false);
                setOpen(true);
              }
            }}
            onFocus={() => !open && !disabled && setOpen(true)}
            onKeyDown={onInputKeyDown}
            inputProps={{
              role: "combobox",
              "aria-label": `Search ${noun}`,
              "aria-expanded": open,
              "aria-controls": "lost-sales-scope-listbox",
              "aria-autocomplete": "list",
            }}
            sx={{
              flex: 1,
              minWidth: 72,
              fontSize: 14,
              "& input::placeholder": { color: "var(--text-secondary)", opacity: 1 },
            }}
          />
          {(value.length > 0 || query) && !disabled && (
            <Tooltip title={query ? "Clear search" : "Clear selection"}>
              <IconButton
                size="small"
                aria-label={query ? "Clear search" : "Clear selection"}
                onClick={(event) => {
                  event.stopPropagation();
                  if (query) setQuery("");
                  else onChange([]);
                }}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            tabIndex={-1}
            aria-label={open ? "Close" : "Open"}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation();
              if (open) close();
              else openMenu();
            }}
            sx={{ p: 0.25 }}
          >
            <ArrowDropDownIcon
              sx={{
                color: "var(--text-secondary)",
                transform: open ? "rotate(180deg)" : "none",
                transition: "transform 120ms",
              }}
            />
          </IconButton>
        </Box>

        <Popper
          open={open}
          anchorEl={fieldRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300 }}
          modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        >
          <Paper
            elevation={8}
            onMouseDown={keepFocus}
            sx={{
              width: Math.min(640, Math.max(380, fieldRef.current?.offsetWidth || 0)),
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Box sx={{ display: "flex", gap: 0.75, px: 1.25, pt: 1.25, pb: 0.75 }}>
              <Chip
                size="small"
                clickable
                label={`All ${noun}`}
                color={showSelectedOnly ? "default" : "primary"}
                variant={showSelectedOnly ? "outlined" : "filled"}
                onClick={() => setShowSelectedOnly(false)}
                sx={{ fontSize: 11.5 }}
              />
              <Chip
                size="small"
                clickable
                label={`Selected · ${value.length}`}
                color={showSelectedOnly ? "primary" : "default"}
                variant={showSelectedOnly ? "filled" : "outlined"}
                onClick={() => setShowSelectedOnly(true)}
                sx={{ fontSize: 11.5 }}
              />
            </Box>
            <Divider />
            <Box
              ref={listRef}
              id="lost-sales-scope-listbox"
              role="listbox"
              aria-multiselectable="true"
              aria-label={isProduct ? "Products" : "SKUs"}
              sx={{ maxHeight: 340, overflowY: "auto", pb: 0.5 }}
            >
              <SectionHeader>
                {showSelectedOnly
                  ? `Selected · ${value.length}`
                  : debouncedQuery
                    ? "Matches"
                    : "Catalog"}
              </SectionHeader>
              {rows.map(renderRow)}
              {rows.length === 0 && renderEmpty()}
              {!showSelectedOnly && rows.length > 0 && catalog.loading && (
                <StatusLine spinner>Searching…</StatusLine>
              )}
              {!showSelectedOnly && catalog.hasMore && !catalog.loading && (
                <Box sx={{ px: 1.25, py: 0.5 }}>
                  <Button
                    size="small"
                    fullWidth
                    onClick={catalog.loadMore}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </Box>
            {selectAllNotice && (
              <>
                <Divider />
                <Typography sx={{ px: 1.5, py: 0.75, fontSize: 12, color: "warning.dark" }}>
                  {selectAllNotice}
                </Typography>
              </>
            )}
            <Divider />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                px: 1.25,
                py: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {!showSelectedOnly && (
                  <Tooltip title={trimmedQuery ? "" : "Type to search, then select every match."}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!canSelectAll}
                        onClick={selectAll}
                        startIcon={
                          selectAllBusy ? <CircularProgress size={12} color="inherit" /> : null
                        }
                        sx={{ textTransform: "none", fontSize: 12 }}
                      >
                        {selectAllBusy ? "Selecting…" : "Select all matching"}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  disabled={!value.length}
                  onClick={() => onChange([])}
                  sx={{ textTransform: "none", fontSize: 12 }}
                >
                  Clear
                </Button>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    color: atCapacity ? "warning.main" : "var(--text-secondary)",
                  }}
                >
                  {value.length} selected
                  {atCapacity ? ` · limit ${maxSelections}` : ""}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={close}
                  sx={{ textTransform: "none", fontSize: 12, boxShadow: "none" }}
                >
                  Done
                </Button>
              </Box>
            </Box>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default LostSalesScopePicker;
